import { zodResolver } from '@hookform/resolvers/zod'
import {
  ApiError,
  type CreateVendingMachineInput,
  createVendingMachineSchema,
  type LocationType,
  queryKeys,
  type VendingMachine,
} from '@mevaser/shared'
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { AlertTriangle, PackageSearch, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import type { z } from 'zod'
import { Page, PageHeader } from '../components/app-shell.js'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

type FormValues = z.input<typeof createVendingMachineSchema>

interface LocationOption {
  id: string
  name: string
  type: LocationType
}

function HealthDot({ ok, label }: { ok: boolean | null; label: string }) {
  return (
    <span className="flex items-center gap-1 text-xs">
      <span
        className={`size-2 rounded-full ${ok === null ? 'bg-neutral-300' : ok ? 'bg-success' : 'bg-danger'}`}
        aria-hidden="true"
      />
      {label}
    </span>
  )
}

export function MachinesPage() {
  const { can } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [creating, setCreating] = useState(false)
  const [deleting, setDeleting] = useState<VendingMachine | null>(null)
  const [showRetired, setShowRetired] = useState(false)
  const canManage = can('kiosks:manage')

  const { data: machines, isLoading } = useQuery({
    queryKey: queryKeys.vending.machines(),
    queryFn: () => api.get<VendingMachine[]>('/vending/machines'),
    refetchInterval: 15_000,
  })

  const retiredCount = machines?.filter((m) => m.status === 'retired').length ?? 0
  const activeCount = (machines?.length ?? 0) - retiredCount
  const visible = showRetired ? machines : machines?.filter((m) => m.status !== 'retired')

  const { data: locations } = useQuery({
    queryKey: queryKeys.locations(),
    queryFn: () => api.get<LocationOption[]>('/locations'),
    enabled: creating,
  })
  const kioskLocations = locations?.filter((l) => l.type === 'kiosk') ?? []

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues, unknown, CreateVendingMachineInput>({
    resolver: zodResolver(createVendingMachineSchema),
    defaultValues: {
      name: '',
      locationId: '',
      alertPhone: null,
      alertEmail: null,
      note: null,
      kind: 'vending',
    },
  })
  const locationId = watch('locationId')
  const kind = watch('kind')

  const create = useMutation({
    mutationFn: (input: CreateVendingMachineInput) =>
      api.post<VendingMachine>('/vending/machines', input),
    onSuccess: (machine) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.vending.machines() })
      toast.success(
        'Machine created',
        machine.kind === 'cabinet'
          ? 'Set the cabinet code and pair its tablet from the machine page.'
          : 'Pair its tablet and Pi from the machine page.',
      )
      setCreating(false)
      reset()
    },
    onError: (error) =>
      toast.error(
        'Could not create the machine',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.delete<{ retired: boolean }>(`/vending/machines/${id}`),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.vending.machines() })
      toast.success(result.retired ? 'Machine retired' : 'Machine deleted')
      setDeleting(null)
    },
    onError: (error) =>
      toast.error(
        'Could not remove the machine',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  return (
    <Page>
      <PageHeader
        title="Vending machines"
        description="A vending machine is a tablet and a Raspberry Pi; a glass cabinet is a tablet next to a combination-locked cabinet. A location can host more than one."
        actions={
          canManage ? (
            <Button onClick={() => setCreating(true)}>
              <Plus /> Add machine
            </Button>
          ) : null
        }
      />

      {retiredCount > 0 ? (
        <div className="mb-3">
          <Button variant="ghost" size="sm" onClick={() => setShowRetired((v) => !v)}>
            {showRetired ? 'Hide retired' : `Show retired (${retiredCount})`}
          </Button>
        </div>
      ) : null}

      {isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }, (_, i) => (
            <Card key={i}>
              <CardContent className="h-32 pt-4" />
            </Card>
          ))}
        </div>
      ) : activeCount === 0 && !showRetired ? (
        <Card>
          <EmptyState
            icon={PackageSearch}
            title="No vending machines yet"
            description={
              retiredCount > 0
                ? 'Retired machines are hidden. Add one, then pair its tablet and Pi from the machine page.'
                : 'Add one, then pair its tablet and Pi from the machine page.'
            }
          />
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {visible?.map((machine) => (
            <Card
              key={machine.id}
              className={`relative transition-colors hover:bg-surface-sunken/60 ${machine.status === 'retired' ? 'opacity-70' : ''}`}
            >
              <CardContent className="pt-4">
                <div className="flex items-start justify-between gap-2">
                  {/* after:absolute after:inset-0 stretches the click target to the whole
                      card; the Delete button below stays on top via relative + DOM order. */}
                  <Link
                    to="/machines/$machineId"
                    params={{ machineId: machine.id }}
                    className="min-w-0 after:absolute after:inset-0"
                  >
                    <p className="truncate font-medium hover:underline">{machine.name}</p>
                    <p className="text-xs text-foreground-muted">
                      {machine.kind === 'cabinet' ? 'Glass cabinet · ' : ''}
                      {machine.locationName}
                    </p>
                  </Link>
                  {canManage && machine.status !== 'retired' ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="relative"
                      aria-label={`Remove ${machine.name}`}
                      onClick={() => setDeleting(machine)}
                    >
                      <Trash2 />
                    </Button>
                  ) : null}
                </div>

                {machine.status === 'retired' ? (
                  <Badge tone="neutral" className="mt-3">
                    Retired
                  </Badge>
                ) : null}
                {machine.status === 'disabled' ? (
                  <Badge tone="warning" className="mt-3">
                    Disabled
                  </Badge>
                ) : null}

                <div
                  className={`mt-3 flex flex-wrap gap-3 ${machine.status === 'retired' ? 'hidden' : ''}`}
                >
                  <HealthDot ok={machine.tabletOnline} label="Tablet" />
                  {machine.kind === 'vending' ? (
                    <>
                      <HealthDot ok={machine.piOnline} label="Pi" />
                      <HealthDot ok={machine.machineOnline} label="Machine" />
                    </>
                  ) : machine.cabinetCode === null && canManage ? (
                    <Badge tone="warning">No cabinet code set</Badge>
                  ) : null}
                </div>

                {machine.openIssueCount > 0 ? (
                  <Badge tone="danger" className="mt-3">
                    <AlertTriangle /> {machine.openIssueCount} open{' '}
                    {machine.openIssueCount === 1 ? 'issue' : 'issues'}
                  </Badge>
                ) : null}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={creating} onOpenChange={(open) => !open && setCreating(false)}>
        <DialogContent className="max-w-md">
          <form onSubmit={handleSubmit((values) => create.mutate(values))}>
            <DialogHeader>
              <DialogTitle>Add a machine</DialogTitle>
            </DialogHeader>
            <DialogBody className="space-y-4">
              <Field label="Type" required>
                {(props) => (
                  <Select
                    value={kind}
                    onValueChange={(value) => setValue('kind', value as 'vending' | 'cabinet')}
                  >
                    <SelectTrigger {...props}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vending">Vending machine (tablet + Pi)</SelectItem>
                      <SelectItem value="cabinet">
                        Glass cabinet (tablet + combination lock)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <Field label="Name" error={errors.name?.message} required>
                {(props) => <Input {...props} {...register('name')} placeholder="Mall Kiosk 1" />}
              </Field>
              <Field label="Location" error={errors.locationId?.message} required>
                {(props) => (
                  <Select
                    value={locationId}
                    onValueChange={(value) => setValue('locationId', value)}
                  >
                    <SelectTrigger {...props}>
                      <SelectValue placeholder="Choose a kiosk-type location…" />
                    </SelectTrigger>
                    <SelectContent>
                      {kioskLocations.map((l) => (
                        <SelectItem key={l.id} value={l.id}>
                          {l.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              {kioskLocations.length === 0 ? (
                <p className="text-xs text-foreground-muted">
                  No kiosk-type locations exist yet — create one from the Locations page first.
                </p>
              ) : null}
              <Field label="Alert phone" error={errors.alertPhone?.message}>
                {(props) => (
                  <Input
                    {...props}
                    {...register('alertPhone', {
                      setValueAs: (v: string | null) => (v?.trim() ? v.trim() : null),
                    })}
                    placeholder="(555) 555-0100"
                  />
                )}
              </Field>
              <Field label="Alert email" error={errors.alertEmail?.message}>
                {(props) => (
                  <Input
                    {...props}
                    {...register('alertEmail', {
                      setValueAs: (v: string | null) => (v?.trim() ? v.trim() : null),
                    })}
                    placeholder="staff@example.com"
                    type="email"
                  />
                )}
              </Field>
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => setCreating(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" loading={create.isPending}>
                Create
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={deleting !== null} onOpenChange={(open) => !open && setDeleting(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Remove {deleting?.name}?</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-foreground-muted">
              If this machine has any orders on record it is retired instead of deleted, keeping its
              history intact. Either way, its tablet and Pi are revoked immediately.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() => deleting && remove.mutate(deleting.id)}
            >
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Page>
  )
}
